import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Address, AddressKind, MailTo, Verification } from "@shared/address";

/** GET /api/companies/:id/addresses (server/address-routes.ts). PUT and DELETE answer with the same shape. */
export interface CompanyAddresses {
  category: string;
  allowsOwnerHome: boolean;
  company: Address | null;
  ownerHome: Address | null;
  mailTo: MailTo | null;
}

/** The PUT body. Parts are text as typed; the server trims them and reads blank as empty. */
export interface AddressInput {
  street: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  addressee?: string;
  verification?: Verification;
  verificationNote?: string;
  verifiedOn?: string;
}

export const companyAddressesKey = (companyId: string) => ["company-addresses", companyId] as const;

/**
 * A company's addresses, with save and remove. Both write the response back
 * into the query, so the panel and its "Packets go to" line change together.
 * It refetches on mount because enrichment, imports, Claude and a category
 * move all change addresses without passing through this hook.
 */
export function useCompanyAddresses(companyId: string) {
  const qc = useQueryClient();
  const key = companyAddressesKey(companyId);
  const query = useQuery<CompanyAddresses>({
    queryKey: key,
    queryFn: async () => (await apiRequest("GET", `/api/companies/${companyId}/addresses`)).json(),
    enabled: !!companyId,
    refetchOnMount: "always",
  });
  const put = (data: CompanyAddresses) => qc.setQueryData(key, data);
  const saveMutation = useMutation({
    mutationFn: async ({ kind, input }: { kind: AddressKind; input: AddressInput }) =>
      (await apiRequest("PUT", `/api/companies/${companyId}/addresses/${kind}`, input)).json() as Promise<CompanyAddresses>,
    onSuccess: put,
  });
  const removeMutation = useMutation({
    mutationFn: async (kind: AddressKind) =>
      (await apiRequest("DELETE", `/api/companies/${companyId}/addresses/${kind}`)).json() as Promise<CompanyAddresses>,
    onSuccess: put,
  });
  return {
    ...query,
    save: (kind: AddressKind, input: AddressInput) => saveMutation.mutateAsync({ kind, input }),
    remove: (kind: AddressKind) => removeMutation.mutateAsync(kind),
  };
}

/**
 * apiRequest throws `Error("400: {\"error\":\"...\",\"field\":\"postalCode\"}")`.
 * This reads the sentence and, for a 400, the input it belongs under. A body
 * that is HTML (a Cloudflare 502 or 524 page) or over 300 characters is not a
 * sentence for a person, so it reads as the fallback.
 */
export function addressSaveError(e: unknown, fallback: string): { field: string | null; message: string } {
  const body = (e instanceof Error ? e.message : "").replace(/^\d{3}:\s*/, "");
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.error === "string") {
      return { field: typeof parsed.field === "string" ? parsed.field : null, message: parsed.error };
    }
  } catch {
    // not JSON
  }
  const readable = body && !/^\s*</.test(body) && body.length <= 300;
  return { field: null, message: readable ? body : fallback };
}
